import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, X, Image, Video, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MediaUploadPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file => 
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    
    if (validFiles.length !== droppedFiles.length) {
      toast.error('Some files were skipped. Only images and videos are allowed.');
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(
        `${API}/api/upload-files/${orderId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.success) {
        setUploadComplete(true);
        toast.success(`Successfully uploaded ${files.length} file(s)!`);
        setTimeout(() => {
          setFiles([]);
          setUploadComplete(false);
          setUploadProgress(0);
        }, 3000);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
              Upload Your Media
            </h1>
            <p className="text-gray-400 text-lg">
              Share your photos and videos for your Visual Creationz project
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Order ID: <span className="font-mono text-amber-500">{orderId}</span>
            </p>
          </div>

          {uploadComplete ? (
            /* Success Message */
            <div className="bg-green-500/10 border-2 border-green-500 rounded-2xl p-12 text-center">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">Upload Complete!</h2>
              <p className="text-gray-300 mb-6">
                Your files have been successfully uploaded. We'll get started on creating your masterpiece!
              </p>
              <button
                onClick={() => {
                  setUploadComplete(false);
                  setFiles([]);
                }}
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-8 py-3 rounded-full font-semibold hover:from-amber-600 hover:to-amber-700 transition-all"
              >
                Upload More Files
              </button>
            </div>
          ) : (
            <>
              {/* Drag & Drop Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-4 border-dashed rounded-2xl p-12 text-center transition-all ${
                  dragActive
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-gray-600 bg-white/5'
                }`}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                <Upload className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                
                <h3 className="text-2xl font-bold mb-2">
                  Drag & Drop Files Here
                </h3>
                <p className="text-gray-400 mb-4">
                  or click the button below to browse
                </p>
                
                <label
                  htmlFor="file-upload"
                  className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 text-black px-8 py-3 rounded-full font-semibold cursor-pointer hover:from-amber-600 hover:to-amber-700 transition-all"
                >
                  Choose Files
                </label>
                
                <p className="text-sm text-gray-500 mt-4">
                  Supported: JPG, PNG, MP4, MOV, AVI, and more
                </p>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-8 bg-white/5 rounded-2xl p-6 border border-white/10">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">
                      Selected Files ({files.length})
                    </h3>
                    <button
                      onClick={() => setFiles([])}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Clear All
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white/5 p-3 rounded-lg border border-white/10"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {file.type.startsWith('image/') ? (
                            <Image className="text-blue-400 flex-shrink-0" size={20} />
                          ) : (
                            <Video className="text-purple-400 flex-shrink-0" size={20} />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-400 hover:text-red-300 p-1 flex-shrink-0"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Upload Button */}
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="w-full mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-full font-bold text-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${files.length} File(s)`}
                  </button>

                  {/* Progress Bar */}
                  {uploading && (
                    <div className="mt-4">
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Info Box */}
              <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                <div className="flex gap-3">
                  <AlertCircle className="text-blue-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold mb-2 text-blue-300">Upload Tips</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• You can upload files multiple times</li>
                      <li>• High-resolution photos and videos work best</li>
                      <li>• No file size limit - upload as much as you need</li>
                      <li>• Files are securely stored in the cloud</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaUploadPage;
